import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Layout,
  Text,
  Badge,
  Button,
  Icon,
  BlockStack,
  InlineStack,
  TextField,
  Tooltip,
  Spinner,
  ButtonGroup,
} from '@shopify/polaris';
import {
  ContentMinor,
  ViewMajor,
  RefreshMajor,
  ChevronLeftMinor,
  ChevronRightMinor,
  AddMajor,
} from '@shopify/polaris-icons';
import '@shopify/polaris/build/esm/styles.css';
import '../modal-overrides.css';
import '../dashboard.css';

// Import services
import { trialsService } from '../services/trialsService';

// Import modal components
import ViewTrialModal from './modals/ViewTrialModal';
import CreateTrialModal from './modals/CreateTrialModal';

const TrialsPage = ({ setToastMessage, setToastActive }) => {
  const headings = [
    'Trial ID',
    'Kid Name',
    'Kid Images',
    'Output',
    'Story',
    'Score',
    'Created Date',
    'Status',
    'Actions',
  ];

  // State for trials
  const [trials, setTrials] = useState([]);
  const [isLoadingTrials, setIsLoadingTrials] = useState(false);
  const [trialsSearchQuery, setTrialsSearchQuery] = useState('');
  const [isViewTrialModalOpen, setIsViewTrialModalOpen] = useState(false);
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [isCreateTrialModalOpen, setIsCreateTrialModalOpen] = useState(false);
  const [regeneratingTrials, setRegeneratingTrials] = useState(new Set());

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [count, setCount] = useState(0);

  // Fetch trials data
  const fetchTrials = useCallback(async () => {
    setIsLoadingTrials(true);
    try {
      const result = await trialsService.getAllTrials({
        page,
        limit,
        q: trialsSearchQuery || undefined,
      });
      setTrials(Array.isArray(result?.data) ? result.data : []);
      if (result) {
        setTotal(Number(result.total || 0));
        setPage(Number(result.page || 1));
        setLimit(Number(result.limit || limit));
        setCount(Number(result.count || (Array.isArray(result?.data) ? result.data.length : 0)));
        setHasNext(Boolean(result.hasNext));
        setHasPrev(Boolean(result.hasPrev));
      }
    } catch (err) {
      console.error('Error fetching trials:', err);
      setTrials([]);
      setTotal(0);
      setPage(1);
      setCount(0);
      setHasNext(false);
      setHasPrev(false);
      setToastMessage('Error fetching trials');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
    } finally {
      setIsLoadingTrials(false);
    }
  }, [page, limit, trialsSearchQuery, setToastMessage, setToastActive]);

  // Fetch trials when component mounts or dependencies change
  useEffect(() => {
    fetchTrials();
  }, [fetchTrials]);

  // Add event listener for Create Trial modal
  useEffect(() => {
    const handleOpenCreateTrialModal = () => {
      setIsCreateTrialModalOpen(true);
    };

    window.addEventListener('openCreateTrialModal', handleOpenCreateTrialModal);
    
    return () => {
      window.removeEventListener('openCreateTrialModal', handleOpenCreateTrialModal);
    };
  }, []);

  // Handler for View Trial modal
  const handleViewTrial = useCallback((trial) => {
    setSelectedTrial(trial);
    setIsViewTrialModalOpen(true);
  }, []);

  const handleRegenerateTrial = async (trial) => {
    try {
      // Add trial to regenerating set
      setRegeneratingTrials(prev => new Set(prev).add(trial.trial_id));
      
      // Call the regenerate API
      await trialsService.regenerateTrial(trial.trial_id, 'Manual regeneration from dashboard');
      
      // Show success toast
      setToastMessage('Trial regeneration started successfully');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      
      // Refresh trials list to get updated status
      await fetchTrials();
      
    } catch (error) {
      console.error('Error regenerating trial:', error);
      setToastMessage('Failed to regenerate trial. Please try again.');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 5000);
    } finally {
      // Remove trial from regenerating set
      setRegeneratingTrials(prev => {
        const newSet = new Set(prev);
        newSet.delete(trial.trial_id);
        return newSet;
      });
    }
  };

  const derivedPageCount = Math.max(1, Math.ceil(Number(total || 0) / Number(limit || 1)));

  const formatCairoDate = (dateString) => {
    if (!dateString) return { date: '-', time: '' };
    try {
      const date = new Date(dateString);
      const datePart = date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        timeZone: 'Africa/Cairo' 
      });
      let timePart = date.toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: true, 
        timeZone: 'Africa/Cairo' 
      });
      timePart = timePart.replace(/am|pm/i, match => match.toUpperCase());
      return { date: datePart, time: timePart };
    } catch {
      return { date: '-', time: '' };
    }
  };

  const filteredTrials = trials.filter(trial => {
    const q = trialsSearchQuery.toLowerCase();
    return (
      (trial.kid_name && trial.kid_name.toLowerCase().includes(q)) ||
      (trial.story && trial.story.toLowerCase().includes(q)) ||
      (trial.trial_id && trial.trial_id.toLowerCase().includes(q)) ||
      (trial._id && trial._id.toLowerCase().includes(q))
    );
  });

  return (
    <Layout style={{ marginBottom: '24px' }}>
      <Layout.Section>
        <Card>
          {/* Header with Create Button and Search Bar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 0 16px' }}>
            {/* Left side - Create button and Refresh */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Button
                primary
                icon={AddMajor}
                onClick={() => setIsCreateTrialModalOpen(true)}
              >
                Create Trial
              </Button>
              {/* Refresh Button */}
              <Button
                icon={RefreshMajor}
                onClick={fetchTrials}
                variant="tertiary"
                tone="base"
                accessibilityLabel="Refresh trials"
              />
            </div>
            
            {/* Search Bar aligned right */}
            {trials.length > 0 && (
              <div style={{ minWidth: 260 }}>
                <TextField
                  label="Search by kid name, story, or trial ID..."
                  value={trialsSearchQuery}
                  onChange={(val) => {
                    setPage(1);
                    setTrialsSearchQuery(val);
                  }}
                  placeholder="Search by kid name, story, or trial ID..."
                  autoComplete="off"
                  prefix={
                    <span className="Polaris-Icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="9" cy="9" r="7.5" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" />
                        <path d="M15 15L18 18" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </span>
                  }
                  labelHidden
                />
              </div>
            )}
          </div>
          
          <div style={{ overflowX: 'auto', marginTop: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              {filteredTrials.length > 0 && (
                <thead>
                  <tr>
                    {headings.map((heading, idx) => (
                      <th key={idx} style={{
                        textAlign: idx === 2 ? 'left' : 'center',
                        padding: '12px 8px',
                        fontWeight: 600,
                        color: '#6d7175',
                        fontSize: 13,
                        background: '#fafbfc',
                        borderBottom: '1px solid #e3e3e3',
                        minWidth: idx === 2 ? 80 : 100,
                      }}>{heading}</th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {isLoadingTrials ? (
                  <tr>
                    <td colSpan="9" style={{ padding: '60px 16px', textAlign: 'center' }}>
                      <BlockStack gap="400" align="center">
                        <Spinner size="large" />
                        <Text variant="bodyMd" tone="subdued">Loading trials...</Text>
                      </BlockStack>
                    </td>
                  </tr>
                ) : filteredTrials.length > 0 ? (
                  filteredTrials.map(trial => {
                    const kidImages = trial.kid_images || [];
                    const status = trial.status || 'pending';
                    const cairoDateObj = formatCairoDate(trial.created_at);
                    
                    return (
                      <tr key={trial._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px 8px', minWidth: 120, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>
                          {trial.trial_id || trial._id}
                        </td>
                        <td style={{ padding: '12px 8px', minWidth: 120, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>
                          {trial.kid_name}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'left' }}>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            {kidImages.slice(0, 3).map((imageUrl, index) => (
                              <div 
                                key={index}
                                style={{ 
                                  width: 32, 
                                  height: 32, 
                                  borderRadius: 4, 
                                  overflow: 'hidden', 
                                  background: '#f6f6f7', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center'
                                }}
                              >
                                <img 
                                  src={imageUrl} 
                                  alt={`${trial.kid_name} ${index + 1}`} 
                                  style={{ width: 32, height: 32, objectFit: 'cover' }} 
                                />
                              </div>
                            ))}
                            {kidImages.length > 3 && (
                              <Text variant="bodySm" tone="subdued">
                                +{kidImages.length - 3}
                              </Text>
                            )}
                            {kidImages.length === 0 && (
                              <Text variant="bodySm" tone="subdued">No images</Text>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'left' }}>
                          {trial.output ? (
                            <div 
                              style={{ 
                                width: 48, 
                                height: 48, 
                                borderRadius: 8, 
                                overflow: 'hidden', 
                                background: '#f6f6f7', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease-in-out'
                              }}
                              onClick={() => handleViewTrial(trial)}
                              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                            >
                              <img 
                                src={trial.output} 
                                alt={`Output for ${trial.kid_name}`} 
                                style={{ width: 48, height: 48, objectFit: 'cover' }} 
                              />
                            </div>
                          ) : (
                            <Text variant="bodySm" tone="subdued">-</Text>
                          )}
                        </td>
                        <td style={{ padding: '12px 8px', minWidth: 120, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>
                          {trial.story}
                        </td>
                        <td style={{ padding: '12px 8px', minWidth: 80, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>
                          {trial.total_score ? `${trial.total_score}%` : '-'}
                        </td>
                        <td style={{ padding: '12px 8px', minWidth: 140, color: '#6d7175', fontSize: 13, textAlign: 'center', lineHeight: 1.2 }}>
                          <span>{cairoDateObj.date}</span><br />
                          <span>{cairoDateObj.time}</span>
                        </td>
                        <td style={{ padding: '12px 8px', minWidth: 100, textAlign: 'center' }}>
                          <Badge tone={status === 'completed' ? 'success' : 'attention'}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </Badge>
                        </td>
                        <td style={{ padding: '12px 8px', minWidth: 120, textAlign: 'center' }}>
                          <InlineStack gap="100" align="center">
                            <Tooltip content="View">
                              <Button
                                icon={ViewMajor}
                                size="slim"
                                onClick={() => handleViewTrial(trial)}
                              />
                            </Tooltip>
                            <Tooltip content="Regenerate">
                              <Button
                                icon={RefreshMajor}
                                size="slim"
                                variant="tertiary"
                                onClick={() => handleRegenerateTrial(trial)}
                                disabled={regeneratingTrials.has(trial.trial_id)}
                                loading={regeneratingTrials.has(trial.trial_id)}
                              />
                            </Tooltip>
                          </InlineStack>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" style={{ padding: '60px 16px', textAlign: 'center' }}>
                      <BlockStack gap="400" align="center">
                        <Icon source={ContentMinor} color="subdued" className="empty-state-big-icon" />
                        <Text variant="bodyMd" tone="subdued">No trials found</Text>
                        <Button
                          primary
                          icon={AddMajor}
                          onClick={() => setIsCreateTrialModalOpen(true)}
                        >
                          Create Your First Trial
                        </Button>
                      </BlockStack>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {(total > 0) && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '24px 0' }}>
              <ButtonGroup variant="segmented">
                <Button
                  icon={ChevronLeftMinor}
                  disabled={!hasPrev || page === 1}
                  onClick={() => hasPrev && setPage(page - 1)}
                />
                {(() => {
                  let start = Math.max(1, page - 2);
                  let end = Math.min(derivedPageCount, start + 4);
                  if (end - start < 4) start = Math.max(1, end - 4);
                  const pages = [];
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <Button
                        key={i}
                        pressed={i === page}
                        onClick={() => setPage(i)}
                        variant={i === page ? 'secondary' : 'secondary'}
                      >
                        {i}
                      </Button>
                    );
                  }
                  return pages;
                })()}
                <Button
                  icon={ChevronRightMinor}
                  disabled={!hasNext || page === derivedPageCount}
                  onClick={() => hasNext && setPage(page + 1)}
                />
              </ButtonGroup>
              <div style={{ marginTop: 8, marginBottom: 16, color: '#6d7175', fontSize: 13 }}>
                {total > 0 ? ((page - 1) * limit) + 1 : 0}
                –
                {total > 0 ? Math.min(((page - 1) * limit) + count, total) : 0}
                {' of '}
                {total}
                {' (Page '}{page}{' of '}{derivedPageCount}{')'}
              </div>
            </div>
          )}
        </Card>
      </Layout.Section>
      
      {/* View Trial Modal */}
      <ViewTrialModal
        isOpen={isViewTrialModalOpen}
        onClose={() => setIsViewTrialModalOpen(false)}
        selectedTrial={selectedTrial}
        setToastMessage={setToastMessage}
        setToastActive={setToastActive}
      />

      {/* Create Trial Modal */}
      <CreateTrialModal
        isOpen={isCreateTrialModalOpen}
        onClose={() => setIsCreateTrialModalOpen(false)}
        setToastMessage={setToastMessage}
        setToastActive={setToastActive}
        onTrialCreated={fetchTrials}
      />
    </Layout>
  );
};

export default TrialsPage;
